/**
 * How many customer threads to fabricate for a type, and an optional brief on what those customers
 * are like. The count stops at what the type's cap leaves room for.
 */

import { useEffect, useState, type FormEvent } from 'react';
import { Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Field, FieldInput } from '@/modules/simulator/ui/field';
import { PillButton } from '@/modules/simulator/ui/pill-button';
import type { FabricationRequest } from './use-fabrication';

const MAX_PER_REQUEST = 10;
const DEFAULT_COUNT = 5;
const MAX_BRIEF_CHARS = 500;

interface FabricateDialogProps {
	open: boolean;
	/** How many more the type takes before its cap. */
	room: number;
	onOpenChange: (open: boolean) => void;
	onFabricate: (request: FabricationRequest) => void;
}

interface FabricateFormProps {
	max: number;
	onCancel: () => void;
	onFabricate: (request: FabricationRequest) => void;
}

/** Mounted with each opening, so it starts fresh then, and counts read again while it is open only clamp it. */
const FabricateForm = ({ max, onCancel, onFabricate }: FabricateFormProps) => {
	const [count, setCount] = useState(String(Math.min(DEFAULT_COUNT, max)));
	const [brief, setBrief] = useState('');

	useEffect(() => {
		setCount((current) => (Number(current) > max ? String(max) : current));
	}, [max]);

	const parsed = Number(count);
	const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= max;
	const submit = (event: FormEvent) => {
		event.preventDefault();
		if (valid) onFabricate({ count: parsed, brief: brief.trim() || null });
	};

	return (
		<form onSubmit={submit} className='flex flex-col gap-4'>
			<DialogHeader>
				<DialogTitle className='text-[15px]'>Fabricate scenarios</DialogTitle>
				<DialogDescription className='text-xs leading-4 text-gray-800'>
					The AI writes customer threads for this scenario type. They run Mocked, beside its past tickets, and have
					no human reply to compare against.
				</DialogDescription>
			</DialogHeader>
			<Field label={`How many (1 to ${max})`}>
				<FieldInput
					type='number'
					inputMode='numeric'
					min={1}
					max={max}
					step={1}
					value={count}
					onChange={(event) => setCount(event.target.value)}
					className='w-24'
				/>
			</Field>
			<Field label='What the customers are like (optional)'>
				<Textarea
					value={brief}
					maxLength={MAX_BRIEF_CHARS}
					placeholder='angry customer, order two weeks late'
					onChange={(event) => setBrief(event.target.value)}
					className='min-h-20 rounded-lg border-gray-500 text-[13px] leading-5'
				/>
			</Field>
			<DialogFooter>
				<PillButton type='button' variant='ghost' onClick={onCancel}>
					Cancel
				</PillButton>
				<PillButton type='submit' variant='primary' disabled={!valid}>
					<Sparkles className='size-3' />
					Fabricate{valid ? ` ${parsed}` : ''}
				</PillButton>
			</DialogFooter>
		</form>
	);
};

export const FabricateDialog = ({ open, room, onOpenChange, onFabricate }: FabricateDialogProps) => (
	<Dialog open={open} onOpenChange={onOpenChange}>
		<DialogContent className='sm:max-w-md'>
			<FabricateForm
				max={Math.max(1, Math.min(MAX_PER_REQUEST, room))}
				onCancel={() => onOpenChange(false)}
				onFabricate={(request) => {
					onFabricate(request);
					onOpenChange(false);
				}}
			/>
		</DialogContent>
	</Dialog>
);
